/*
  gift-reconciler.js  (Wilton London)

  Keeps free-gift lines in sync with their anchor products, no matter how the
  cart was changed (theme form, Skio plan picker, slide cart, cart page,
  another tab). Instead of intercepting adds, it repeatedly makes the cart
  match a simple rule:

    For every non-gift line whose product has a gift mapping (from the
    gift-map section, driven by the same metafields as the PDP tiles):
      - line has a selling plan  -> it should have the "sub" gift variants
      - line has no selling plan -> it should have the "otp" gift variants
    One gift per unit of the anchor. Gift lines carry _bundle_role=gift.

  Anything tagged as a gift that no anchor justifies is removed. Untagged
  lines of the gift products themselves (added via their URL) are removed
  too; any other product the customer bought normally is never touched.

  Runs after any cart mutation, on page load, and on Skio's own add event.
  Idempotent: once the cart matches, a run makes no changes.

  Kill switch: append ?gift-reconcile=off to any URL, or set
  window.__wiltonGiftReconcilerOff = true before this script loads.
*/
(function () {
  'use strict';

  if (window.__wiltonGiftReconciler) return;
  window.__wiltonGiftReconciler = true;

  if (window.__wiltonGiftReconcilerOff) return;
  if (/[?&]gift-reconcile=off\b/.test(window.location.search)) return;

  var GIFT_ROLE = 'gift';
  /* Oct26: was 700. The theme's own drawer is the basket now, so there is no third-party drawer to wait for.
     The double read in run() still guards against acting on a basket that is mid-change. */
  var DEBOUNCE_MS = 300;
  var DRAWER_SECTIONS = 'cart-items,cart-footer,cart-item-count,cart-live-region';

  var timer = null;
  var running = false;
  var runAgain = false;

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(run, DEBOUNCE_MS);
  }

  function getJSON(url, opts) {
    var o = opts || {};
    o.credentials = 'same-origin';
    return fetch(url, o).then(function (r) {
      if (!r.ok) throw new Error(url + ' -> HTTP ' + r.status);
      return r.json();
    });
  }

  function postJSON(url, body) {
    return getJSON(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });
  }

  function fetchCart() {
    return getJSON('/cart.js');
  }

  function fetchGiftMap() {
    return getJSON('/?sections=gift-map&_=' + Date.now()).then(function (sections) {
      var html = (sections && sections['gift-map']) || '';
      if (!html) return {};
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector('[data-gift-map]');
      if (!el) return {};
      try {
        return JSON.parse(el.textContent) || {};
      } catch (e) {
        return {};
      }
    });
  }

  function isGiftLine(item) {
    return !!(item && item.properties && String(item.properties._bundle_role) === GIFT_ROLE);
  }

  /* variant_id -> quantity the cart SHOULD contain as gifts */
  function desiredGifts(cart, map) {
    var want = {};
    (cart.items || []).forEach(function (item) {
      if (isGiftLine(item)) return;
      var m = map[String(item.product_id)];
      if (!m || !m.sub) return;
      var list = item.selling_plan_allocation ? m.sub : m.otp;
      (list || []).forEach(function (vid) {
        vid = String(vid);
        want[vid] = (want[vid] || 0) + (item.quantity || 1);
      });
    });
    return want;
  }

  /* variant_id -> { qty, lines:[{key, qty}] } of tagged gift lines present */
  function actualGifts(cart) {
    var have = {};
    (cart.items || []).forEach(function (item) {
      if (!isGiftLine(item)) return;
      var vid = String(item.variant_id);
      if (!have[vid]) have[vid] = { qty: 0, lines: [] };
      have[vid].qty += item.quantity || 0;
      have[vid].lines.push({ key: item.key, qty: item.quantity || 0 });
    });
    return have;
  }

  /* Repaint after we change the cart.
     1) AMP Slide Cart is the visible basket (theme cart_type = page), so
        hand it the fresh cart via its own API. SET_CART with data avoids a
        refetch and the loading ring; UPDATE is the fallback.
     2) Also dispatch the theme's own event with the mutation-response
        sections, which keeps the header badge and any theme cart UI in
        sync. Never cached: it comes from the mutation response itself. */
  function refreshUI(data) {
    if (typeof window.SLIDECART_SET_CART === 'function' || typeof window.SLIDECART_UPDATE === 'function') {
      getJSON('/cart.js').then(function (cart) {
        try {
          if (typeof window.SLIDECART_SET_CART === 'function') {
            window.SLIDECART_SET_CART(cart);
          } else if (typeof window.SLIDECART_UPDATE === 'function') {
            window.SLIDECART_UPDATE(function () {});
          }
        } catch (e) { /* no-op */ }
      }).catch(function () {});
    }

    if (!data || !data.sections) return;
    var detail = { response: { sections: data.sections } };
    try {
      if (document.body) {
        document.body.dispatchEvent(
          new CustomEvent('cascade:modalcart:afteradditem', { bubbles: true, detail: detail })
        );
      }
    } catch (e) { /* no-op */ }
  }

  /* Stable fingerprint of cart contents, used to detect mid-flight changes */
  function cartFingerprint(cart) {
    return (cart.items || [])
      .map(function (i) { return i.key + ':' + i.quantity; })
      .sort()
      .join('|');
  }

  function computePlan(cart, map) {
    var want = desiredGifts(cart, map);
    var have = actualGifts(cart);

    var adds = [];
    var updates = {};

    /* Missing or excess gifts for variants we do want */
    Object.keys(want).forEach(function (vid) {
      var haveQty = have[vid] ? have[vid].qty : 0;
      var diff = want[vid] - haveQty;
      if (diff > 0) {
        adds.push({
          id: Number(vid),
          quantity: diff,
          properties: { _bundle_role: GIFT_ROLE }
        });
      } else if (diff < 0) {
        var trim = -diff;
        var lines = have[vid].lines.slice().reverse();
        for (var i = 0; i < lines.length && trim > 0; i++) {
          var cut = Math.min(lines[i].qty, trim);
          updates[lines[i].key] = lines[i].qty - cut;
          trim -= cut;
        }
      }
    });

    /* Orphaned gifts: tagged as gift, but nothing in the cart wants them */
    Object.keys(have).forEach(function (vid) {
      if (want[vid]) return;
      have[vid].lines.forEach(function (line) {
        updates[line.key] = 0;
      });
    });

    /* Untagged copies of gift products (added via the product URL, not by
       an offer) are not legitimate purchases at these prices: remove them. */
    var known = {};
    (map._known || []).forEach(function (vid) { known[String(vid)] = true; });
    (cart.items || []).forEach(function (item) {
      if (isGiftLine(item)) return;
      if (known[String(item.variant_id)]) {
        updates[item.key] = 0;
      }
    });

    return { adds: adds, updates: updates };
  }

  function planFingerprint(plan) {
    return JSON.stringify(plan.adds) + '~' + JSON.stringify(plan.updates);
  }

  function run() {
    if (running) {
      runAgain = true;
      return;
    }
    running = true;

    var map;

    /* Read 1: compute what we'd change */
    Promise.all([fetchCart(), fetchGiftMap()])
      .then(function (results) {
        var cartA = results[0] || { items: [] };
        map = results[1] || {};
        var planA = computePlan(cartA, map);

        if (!planA.adds.length && !Object.keys(planA.updates).length) {
          return null; /* already in sync, touch nothing */
        }

        /* Read 2: confirm the cart hasn't moved underneath us. If the
           customer removed or added something mid-computation, we act on
           the fresh state next cycle instead of firing a stale plan
           (this is what caused gifts to flash in and out). */
        return fetchCart().then(function (cartB) {
          if (cartFingerprint(cartA) !== cartFingerprint(cartB)) {
            runAgain = true;
            return null;
          }
          var planB = computePlan(cartB, map);
          if (planFingerprint(planA) !== planFingerprint(planB)) {
            runAgain = true;
            return null;
          }
          return planB;
        });
      })
      .then(function (plan) {
        if (!plan) return;
        var lastData = null;
        var chain = Promise.resolve();
        if (Object.keys(plan.updates).length) {
          chain = chain.then(function () {
            return postJSON('/cart/update.js', {
              updates: plan.updates,
              sections: DRAWER_SECTIONS,
              sections_url: window.location.pathname
            }).then(function (d) { lastData = d; });
          });
        }
        if (plan.adds.length) {
          chain = chain.then(function () {
            return postJSON('/cart/add.js', {
              items: plan.adds,
              sections: DRAWER_SECTIONS,
              sections_url: window.location.pathname
            }).then(function (d) { lastData = d; });
          });
        }
        return chain.then(function () { refreshUI(lastData); });
      })
      .catch(function (err) {
        if (window.console && console.warn) console.warn('[gift-reconciler]', err);
      })
      .then(function () {
        running = false;
        if (runAgain) {
          runAgain = false;
          schedule();
        }
      });
  }

  /* ---- triggers: observe cart mutations without altering them ---- */

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  var origFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = (init && init.method) || (input && input.method) || 'GET';
    var p = origFetch(input, init);
    if (String(method).toUpperCase() === 'POST' && CART_MUTATION.test(String(url))) {
      p.then(schedule, function () {});
    }
    return p;
  };

  if (typeof XMLHttpRequest !== 'undefined' && XMLHttpRequest.prototype) {
    var XO = XMLHttpRequest.prototype;
    var origOpen = XO.open;
    var origSend = XO.send;
    XO.open = function (method, url) {
      this.__wgrMethod = method;
      this.__wgrUrl = typeof url === 'string' ? url : String(url);
      return origOpen.apply(this, arguments);
    };
    XO.send = function () {
      if (
        String(this.__wgrMethod || '').toUpperCase() === 'POST' &&
        CART_MUTATION.test(String(this.__wgrUrl || ''))
      ) {
        this.addEventListener('loadend', schedule, { once: true });
      }
      return origSend.apply(this, arguments);
    };
  }

  /* Skio announces its own adds */
  document.addEventListener('skio::added-to-cart', schedule, true);

  /* Returning to the page (bfcache, tab switch) can carry a changed cart */
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) schedule();
  });

  /* First pass on load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
